<!-- ============================================================
     Shared templates — referenced in both branches
     ============================================================ -->

<!-- Email verification banner — shown only when emailVerified === false AND not dismissed -->
<ng-template #bannerTpl>
  @if (showBanner()) {
  <div data-testid="email-verification-banner"
    class="relative flex items-center justify-between gap-3 border-b border-brand-orange-300 bg-brand-orange-200 px-4 py-2.5 text-sm sm:px-6 lg:px-8"
    role="alert" aria-live="polite">
    <!-- Icon + Copy -->
    <div class="flex items-center gap-2.5 min-w-0">
      <!-- Warning SVG -->
      <svg class="h-5 w-5 shrink-0 text-brand-orange-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
        fill="currentColor" aria-hidden="true">
        <path fill-rule="evenodd"
          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
          clip-rule="evenodd" />
      </svg>

      <span class="font-futura-medium text-brand-navy">
        Confirma tu email para publicar vacantes.
      </span>

      <!-- Resend button -->
      <button data-testid="banner-resend-btn" type="button" (click)="resendVerification()"
        class="shrink-0 font-futura-bold text-brand-orange-500 underline underline-offset-2 hover:text-brand-orange-600">
        Reenviar correo
      </button>

      <!-- Ephemeral feedback -->
      @if (resendFeedback()) {
      <span class="font-futura-medium text-xs text-brand-navy opacity-80">
        {{ resendFeedback() }}
      </span>
      }
    </div>

    <!-- Dismiss button -->
    <button data-testid="banner-dismiss-btn" type="button" (click)="dismissBanner()"
      class="shrink-0 rounded p-1 text-brand-navy hover:bg-brand-orange-300/40" aria-label="Cerrar aviso">
      <!-- X SVG -->
      <svg class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path
          d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
      </svg>
    </button>
  </div>
  }
</ng-template>

<!-- Header - homologado con la landing -->
<ng-template #headerTpl>
  <header class="sticky top-0 z-50 border-b border-brand-border bg-white">
    <div class="container mx-auto flex items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
      <!-- D-8 (REQ-RSENT-060): con navSuppressed activo, el logo queda como <img> sin routerLink
           — el subscriptionGuard rebotaría ese destino de vuelta a /plan (bucle, R6). -->
      @if (navSuppressed()) {
      <img src="https://storage.googleapis.com/tribuu-public/assets/images/logo.png" alt="Tribuu"
        class="h-10 w-auto object-contain sm:h-11 lg:h-12" />
      } @else {
      <a routerLink="/reclutador/vacantes" class="inline-flex shrink-0">
        <img src="https://storage.googleapis.com/tribuu-public/assets/images/logo.png" alt="Tribuu"
          class="h-10 w-auto object-contain sm:h-11 lg:h-12" />
      </a>
      }
      <div class="flex items-center gap-3">
        @if (isAuthenticated()) {
        @if (!navSuppressed()) {
        <rec-notification-bell></rec-notification-bell>
        }

        <tui-hosted-dropdown [content]="userMenu" aria-label="Menú de usuario"
          class="cursor-pointer rounded-full focus-within:ring-2 focus-within:ring-brand-orange-300 focus-within:ring-offset-2 focus-within:ring-offset-white">
          <tui-avatar data-testid="user-avatar" size="s" [text]="userInitial()" [autoColor]="true" [rounded]="true">
          </tui-avatar>
        </tui-hosted-dropdown>
        }
      </div>
    </div>
  </header>
</ng-template>

<!-- User menu dropdown template — must be available globally for tui-hosted-dropdown -->
<ng-template #userMenu>
  <tui-data-list role="menu">
    <!-- D-8 (REQ-RSENT-060): "Créditos" y "Reportes" son escapes que el subscriptionGuard
         rebotaría — se ocultan con navSuppressed activo. "Reportes" además solo existe con el
         flag environment.reports. "Cerrar sesión", debajo, NUNCA se oculta: es la única
         salida deliberada de la pantalla obligatoria. -->
    @if (!navSuppressed()) {
    <button tuiOption type="button" role="menuitem" data-testid="user-menu-credits"
      class="font-futura-medium !text-sm text-brand-navy" routerLink="/reclutador/creditos">
      Créditos
    </button>
    @if (reportsEnabled) {
    <button tuiOption type="button" role="menuitem" data-testid="user-menu-reports"
      class="font-futura-medium !text-sm text-brand-navy" routerLink="/reclutador/reportes">
      Reportes
      @if (!reportsVisited()) {
      <span data-testid="user-menu-reports-new"
        class="ml-2 rounded-full bg-brand-orange-500 px-2 py-0.5 font-futura-bold text-xxs uppercase text-white">
        Nuevo
      </span>
      }
    </button>
    }
    }
    <button tuiOption type="button" role="menuitem" data-testid="user-menu-logout"
      class="font-futura-medium !text-sm text-brand-navy" (click)="signOut()">
      Cerrar sesión
    </button>
  </tui-data-list>
</ng-template>

<!-- ============================================================
     Branch: full-screen app-shell (onboarding and similar routes)
     - Fixed viewport height, no footer, content fills rest
     ============================================================ -->
@if (isFullScreen()) {
<div class="flex h-screen flex-col overflow-hidden bg-white">
  <div class="shrink-0">
    <ng-container [ngTemplateOutlet]="bannerTpl"></ng-container>
    <ng-container [ngTemplateOutlet]="headerTpl"></ng-container>
  </div>
  <main class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <router-outlet></router-outlet>
  </main>
</div>
} @else {
<!-- ============================================================
     Branch: document mode (vacantes, creditos, notificaciones, etc.)
     - Unchanged behavior: window scroll, scrollPositionRestoration, footer
     ============================================================ -->
<ng-container [ngTemplateOutlet]="bannerTpl"></ng-container>
<ng-container [ngTemplateOutlet]="headerTpl"></ng-container>

<!-- Page Content -->
<main class="min-h-screen bg-white antialiased overflow-x-clip text-black">
  <router-outlet></router-outlet>
</main>

<!-- Footer -->
<footer class="border-t border-brand-border bg-white py-4">
  <div class="container mx-auto flex flex-col items-center gap-4 px-4 sm:px-6 lg:px-8">
    <p class="font-futura-medium text-center text-sm text-gray-tribuu-700">
      ©2026 TRIBUU®. Todos los derechos reservados.
    </p>
  </div>
</footer>
}
